import _ from 'lodash'
import ReactDOM from 'react-dom'
import { forwardRef, useMemo, isValidElement } from 'react'
import { List } from '@fluentui/react/lib/List'
import { ItemCell } from '../../itemCell/ItemCell'
import { Draggable } from 'react-beautiful-dnd'
import { GhostItemCell } from '../../itemCell/GhostItemCell'

export const BasicItemList = forwardRef(
  (
    {
      items,
      onClickItem,
      onCheckboxChange,
      selectedItemId,
      readOnly = false,
      innerRef,
      draggable = false,
      narrow = false,
      placeholder,
      itemCellStyles,
      loading = false,
      shouldShowProject,
      showOwner = true,
      ...rest
    },
    ref
  ) => {
    const _draggable = !readOnly && draggable
    const itemToCell = (item, idx) => {
      if (isValidElement(item)) return item
      if (!item || !item.get) {
        return <GhostItemCell key={idx} />
      }
      const id = item.get('id')
      const selected = id === selectedItemId
      if (_draggable) {
        const draggableId = id.toString() // it needs to be a string
        return (
          <Draggable draggableId={draggableId} index={idx} key={draggableId}>
            {(provided, snapshot) => (
              <ItemCell
                allItemsInList={items}
                shouldShowProject={shouldShowProject}
                item={item}
                idx={idx}
                onClick={onClickItem}
                styles={itemCellStyles}
                onCheckboxChange={onCheckboxChange}
                selected={selected}
                narrow={narrow}
                readOnly={readOnly}
                innerRef={provided.innerRef}
                isDragging={snapshot.isDragging}
                showOwner={showOwner}
                {...provided.dragHandleProps}
                {...provided.draggableProps}
              />
            )}
          </Draggable>
        )
      }
      return (
        <ItemCell
          item={item}
          idx={idx}
          onClick={onClickItem}
          styles={itemCellStyles}
          onCheckboxChange={onCheckboxChange}
          selected={selected}
          narrow={narrow}
          readOnly={readOnly}
          allItemsInList={items}
          shouldShowProject={shouldShowProject}
          showOwner={showOwner}
        />
      )
    }
    const refGetter = comp => {
      const div = ReactDOM.findDOMNode(comp)
      if (_.isFunction(ref)) {
        ref(comp)
      } else if (ref) {
        ref.current = comp
      }
      if (_.isFunction(innerRef)) {
        innerRef(div)
      }
    }
    // disabling getItemCountForPage and getPageHeight since they're not working properly...
    const itemsForList = useMemo(() => {
      const res = [...items]
      if (loading) {
        res.push(...Array(25).fill(null))
      }
      if (placeholder) {
        res.push(placeholder)
      }
      return res
      // Change item list when selected item changes to trigger re-render:
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, loading, placeholder, selectedItemId])
    return <List items={itemsForList} onRenderCell={itemToCell} componentRef={refGetter} {...rest} />
  }
)
